import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LOGO_URL, ICON_URL, HERO_ANIMATION_URL, api, auth } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, LogIn, Plus, Zap, Loader2 } from "lucide-react";

export default function Hero() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const [botInvite, setBotInvite] = useState("");

  useEffect(() => {
    api.get("/auth/bot-invite").then((r) => setBotInvite(r.data.url || "")).catch(() => {});
  }, []);

  const triggerLogin = async (demo) => {
    const setBusy = demo ? setDemoLoading : setLoading;
    setBusy(true);
    try {
      const { data } = await api.get(`/auth/discord/login${demo ? "?demo=true" : ""}`);
      if (data.mode === "demo") {
        auth.setSession(data.token, data.user);
        toast.success("Demo session active — exploring as Commander");
        navigate("/dashboard");
      } else if (data.mode === "oauth") {
        // Break out of iframe (Emergent preview) — Discord blocks iframe embedding
        try { window.top.location.href = data.url; }
        catch { window.open(data.url, "_blank", "noopener"); }
      }
    } catch (e) {
      toast.error("Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="hero-section" className="relative overflow-hidden border-b border-brand-line tactical-grid">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-20 pb-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 stagger">
            <div className="inline-flex items-center gap-2 border border-brand-orange/40 px-3 py-1.5 rounded-full">
              <span className="status-dot status-online" />
              <span className="overline">BOT ONLINE · GEMINI 3 FLASH</span>
            </div>

            <h1 className="mt-6 font-heading text-5xl sm:text-6xl lg:text-7xl font-bold uppercase tracking-tight leading-[0.95]">
              YOUR DISCORD,
              <br />
              <span className="text-brand-orange text-glow-orange">FULLY WEAPONIZED.</span>
            </h1>

            <p className="mt-6 text-zinc-400 text-base md:text-lg max-w-xl leading-relaxed">
              Add the <span className="text-white font-semibold">606 Bot</span> to your server in one click. Music, moderation, and a multilingual AI sidekick — controlled from a single esports-grade dashboard.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              {botInvite && (
                <a
                  href={botInvite}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-primary text-base"
                  data-testid="hero-add-bot-btn"
                >
                  <Plus className="w-4 h-4" /> ADD BOT TO SERVER
                  <ArrowRight className="w-4 h-4" />
                </a>
              )}
              <button onClick={() => triggerLogin(false)} disabled={loading} className="btn-secondary text-base" data-testid="hero-discord-login-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
                Login with Discord
              </button>
              <button onClick={() => triggerLogin(true)} disabled={demoLoading} className="btn-ghost border border-brand-line hover:border-brand-orange text-base" data-testid="hero-demo-btn">
                {demoLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Explore Demo
              </button>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-6 max-w-md">
              <div>
                <div className="font-mono-tabular text-2xl font-bold text-brand-orange">FREE</div>
                <div className="overline">Tier</div>
              </div>
              <div>
                <div className="font-mono-tabular text-2xl font-bold text-brand-orange">∞</div>
                <div className="overline">Languages</div>
              </div>
              <div>
                <div className="font-mono-tabular text-2xl font-bold text-brand-orange">1-Click</div>
                <div className="overline">Install</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative">
              <div className="absolute -inset-6 bg-brand-orange/10 blur-3xl rounded-full" />
              <div className="relative tactical-card p-6 clip-corner overflow-hidden">
                <div className="flex items-center justify-between mb-4">
                  <div className="overline">SYSTEM SYNC · 606-CORE</div>
                  <Zap className="w-4 h-4 text-brand-orange" />
                </div>
                <div className="relative aspect-square w-full bg-black rounded-xl overflow-hidden">
                  <img src={HERO_ANIMATION_URL} alt="606 Community Pulse" className="w-full h-full object-contain" data-testid="hero-animation" />
                </div>
                <div className="mt-5 grid grid-cols-2 gap-3 text-xs font-mono-tabular">
                  <div className="border border-brand-line p-3 rounded-lg">
                    <div className="text-zinc-500">STATUS</div>
                    <div className="text-emerald-400 mt-1">● OPERATIONAL</div>
                  </div>
                  <div className="border border-brand-line p-3 rounded-lg">
                    <div className="text-zinc-500">VOICE</div>
                    <div className="text-white mt-1">FFmpeg · OK</div>
                  </div>
                  <div className="border border-brand-line p-3 rounded-lg">
                    <div className="text-zinc-500">LATENCY</div>
                    <div className="text-white mt-1">42ms</div>
                  </div>
                  <div className="border border-brand-line p-3 rounded-lg">
                    <div className="text-zinc-500">VERSION</div>
                    <div className="text-white mt-1">v1.2.0</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
